import request from 'superagent'
import get from 'lodash/get'
import mapValues from 'lodash/mapValues'
import merge from 'lodash/merge'
import isEmpty from 'lodash/isEmpty'

import { helpers } from 'react-wot-bootstrap'
import { BATTLE_TYPES } from 'utils/clan_management/battles'

export class RequestsManager {
    static requests = []

    static clearAll() {
        RequestsManager.requests = []
    }

    static abortAll() {
        let isAborted = false
        RequestsManager.requests.forEach(req => {
            if (req.xhr.readyState !== 4) {
                isAborted = true
                req.abort()
            }
        })
        RequestsManager.clearAll()
        return isAborted
    }

    static add(req) {
        RequestsManager.requests.push(req)
    }
}

export const fetch = (url, options = {}, isCancelable = false) => {
    const headers = {
        ...options.headers,
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
    }
    const method = options.method || 'GET'

    let req = request(method, url)
        .set(headers)
        .withCredentials()

    if (isCancelable) RequestsManager.add(req)

    if (!isEmpty(options.query)) {
        req = req.query(options.query)
    } else if (!isEmpty(options.body)) {
        req = req.send(options.body)
    }

    return req
        .then(response => response.body)
        .catch((response) => {
            if ([401, 403, 409].includes(response.status)) {
                throw response.response.body
            }
            throw response
        })
}

export const reflect = (promise) => promise.then(json => ({ json })).catch(error => ({ error }))

export const parseResult = (result, defaultNotFoundData) => {
    if (result.error || 'error' in result.json) {
        if (get(result, ['error', 'status']) === 404 && defaultNotFoundData !== undefined) {
            return defaultNotFoundData
        }
        throw result.error || result.json
    }
    return result.json
}

const isNumber = n => !isNaN(parseFloat(n)) && isFinite(n)

const safeDiv = (a, b) => {
    if (isNumber(a) && isNumber(b)) {
        if (a && b) return a / b
        return 0
    }
    return null
}

const calculateStatistics = (data, additional = {}) => {
    let winsPercentage = safeDiv(data.wins_count, data.battles_count)
    winsPercentage = isNumber(winsPercentage) ? winsPercentage * 100 : null

    return {
        battlesCount: data.battles_count,
        damagePerBattle: safeDiv(data.damage_dealt, data.battles_count),
        expPerBattle: safeDiv(data.xp_amount, data.battles_count),
        winsCount: data.wins_count,
        winsPercentage,
        ...additional,
    }
}

const parseExporterResponse = (response, battleType = BATTLE_TYPES.RANDOM) => {
    if (!Array.isArray(response)) return {}

    const result = {}
    response.forEach(data => {
        const section = data[battleType] || {}
        result[data.account_id] = calculateStatistics(section, {
            personalRating: data.global_rating,
            lastBattleTime: data.last_battle_time || 0,
        })
    })

    return result
}

const parseWgrsResponse = (response) => {
    if (!Array.isArray(response)) return {}

    const result = {}
    response.forEach(data => {
        result[data.account_id] = calculateStatistics(data)
    })

    return result
}

export const combineStatistics = (exporter, wgrs, battleType) => {
    const exporterStatistics = parseExporterResponse(exporter, battleType)
    const wgrsRatings = parseWgrsResponse(wgrs)

    const statistics = merge(exporterStatistics, wgrsRatings)

    return mapValues(statistics, (account, id) => {
        return {
            ...account,
            personalRating: get(exporterStatistics, [id, 'personalRating'], 0),
            lastBattleTime: get(exporterStatistics, [id, 'lastBattleTime'], 0),
        }
    })
}

export const snakeToCamel = (string) => {
    return string.replace(/(\_[a-z])/g, (item) => item.toUpperCase().replace('_', ''))
}

/**
 * returns object with all keys camel-cased
 * @example: normalizeClan({ clan_id: 1 }) === { clanId: 1 }
 */
export const normalizeClan = (clan) => {
    const result = {}
    Object.keys(clan).forEach(key => {
        result[snakeToCamel(key)] = clan[key]
    })

    return result
}

export const processSpaResponse = (spaResult, clanMembers, accountId) => {
    const spa = {}
    spaResult.forEach(account => {
        spa[account.id] = account.name
    })

    const members = []
    clanMembers.forEach(member => {
        const spaAccount = spa[member.id]
        if (!spaAccount) {
            // eslint-disable-next-line no-console
            console.error(`Can not find ${member.id} in SPA response`)
            return
        }
        members.push({
            ...member,
            name: spaAccount,
            daysInClan: helpers.daysSinceDate(member.joined_at),
            joined_at: undefined,
        })
    })

    const ownMember = members.find((member) => member.id === accountId)
    if (!ownMember) {
        // eslint-disable-next-line no-console
        console.error('Current account not found in members list')
        const error = new Error('Current account not found in members list')
        error.reason = 'account_is_not_in_clan'
        throw error
    }

    return members
}

export const annotateMembersWithLogoutInfo = (members, data) => {
    const logoutData = {}
    data.forEach(account => {
        logoutData[account.account_id] = account.logout_at
    })

    return members.map(member => ({
        ...member,
        logoutAt: logoutData[member.id],
    }))
}

export function annotateMembersWithAccountStateInfo(members, statistics) {
    return members.map(member => ({
        ...member,
        isSuspended: statistics[member.id].isSuspended,
        isAnonymized: statistics[member.id].isAnonymized,
    }))
}

export const getError = (e) => {
    if (e instanceof Error) return e

    const error = e.data ? new Error(e.data.description) : new Error(e.message || e.description || e.title)
    error.response = e
    return error
}

export const isServerError = (e) => {
    return e && e.response !== undefined
}

export const normalizeStatisticsByDefaultValue = (members, statistics, defaultStatistics) => {
    members.forEach(member => {
        statistics[member.id] = {
            ...defaultStatistics,
            ...statistics[member.id],
        }
    })
}



// WEBPACK FOOTER //
// ./containers/ClanManagement/proxy/helpers.js